<template>
    <form
        class="form basic-form trip-form"
        autocomplete="off"
        novalidate
        @submit="emitSubmit"
    >
        <!-- name -->
        <div class="form-field">
            <label for="name" class="required">Trip name:</label>
            <input
                id="name"
                v-model="form.name.value"
                type="text"
                maxlength="100"
            />
            <FormError :errors="errors.name" />
        </div>

        <!-- privacy -->
        <div v-if="false" class="form-field">
            <label for="privacy" class="required">Who can see this?</label>
            <EnumRadio v-model="form.privacy.value" group="privacy" />
            <FormError :errors="errors.privacy" />
        </div>

        <!-- start_date & end date -->
        <div class="two-fields">
            <div class="form-field">
                <label for="start-date" class="required">Start date:</label>
                <input
                    id="start-date"
                    v-model="form.start_date.value"
                    type="date"
                    @change="setEndDate($event)"
                />
                <FormError :errors="errors.start_date" />
            </div>
            <div class="form-field">
                <label for="end-date">End date:</label>
                <input
                    id="end-date"
                    v-model="form.end_date.value"
                    type="date"
                />
                <FormError :errors="errors.end_date" />
            </div>
        </div>

        <!-- reason -->
        <div class="form-field">
            <label for="reason" class="required">Reason for traveling:</label>
            <Dropdown
                id="reason"
                v-model="form.reason.value"
                group="travel_reason"
            ></Dropdown>
            <FormError :errors="errors.reason" />
        </div>

        <!-- companions -->
        <div class="form-field">
            <label for="companions">Travel companions:</label>
            <MultiselectableDropdown
                id="companions"
                v-model="form.companions.value"
                insertable
                typable
                new-prompt-text="Start adding companions by typing in their names"
                :options="companionOptions"
                :loaded="formValuesSet"
            ></MultiselectableDropdown>
            <FormError :errors="errors.companions" />
        </div>

        <!-- notes -->
        <div class="form-field">
            <label for="notes">Trip notes:</label>
            <textarea id="notes" v-model="form.notes.value" rows="4" />
            <FormError :errors="errors.notes" />
        </div>

        <!-- travel_agency -->
        <div class="form-field">
            <label for="travel-agency">Travel agency:</label>
            <input
                id="travel-agency"
                v-model="form.travel_agency.value"
                type="text"
            />
            <FormError :errors="errors.travel_agency" />
        </div>

        <div class="form-field">
            <label for="user_image_file">Image:</label>
            <ImageUpload
                id="user_image_file"
                v-model="form.user_image_file.value"
                name="user_image_file"
                :img="trip && trip.user_image_url"
            />
            <FormError :errors="errors.user_image_file" />
        </div>

        <!-- locations -->
        <h3 v-if="!trip" class="field-title">Locations</h3>
        <div v-if="!trip" class="form-field">
            <FormError :errors="errors.locations" />
            <QuickLocations
                v-model="form.locations.value"
                :start-date="form.start_date.value"
                :end-date="form.end_date.value"
            />
        </div>

        <div v-if="!showMot" class="form-field">
            <span
                style="text-decoration: underline; cursor: pointer"
                @click="showMot = true"
            >
                Do you track means of transport on your trips?
            </span>
        </div>

        <!-- start_mot -->
        <h3 v-if="showMot" class="field-title">Transport on the way there</h3>
        <div v-if="showMot" class="two-fields">
            <div class="form-field">
                <label for="start-mot">Main transport:</label>
                <select id="start-mot" v-model="form.start_mot.value">
                    <EnumOptions group="transport" />
                </select>
                <FormError :errors="errors.start_mot" />
            </div>
            <div class="form-field">
                <label for="start-mot-count">Number of transfers:</label>
                <input
                    id="start-mot-count"
                    v-model="form.start_mot_count.value"
                    type="number"
                />
                <small>
                    If you didn't have any layovers, you should leave it at 1.
                </small>
                <FormError :errors="errors.start_mot_count" />
            </div>
        </div>

        <!-- end_mot -->
        <h3 v-if="showMot" class="field-title">Transport on the way back</h3>
        <div v-if="showMot" class="two-fields">
            <div class="form-field">
                <label for="end-mot">Main transport:</label>
                <select id="end-mot" v-model="form.end_mot.value">
                    <EnumOptions group="transport" />
                </select>
                <FormError :errors="errors.end_mot" />
            </div>
            <div class="form-field">
                <label for="end-mot-count">Number of transfers:</label>
                <input
                    id="end-mot-count"
                    v-model="form.end_mot_count.value"
                    type="number"
                />
                <small>
                    If you didn't have any layovers, you should leave it at 1.
                </small>
                <FormError :errors="errors.end_mot_count" />
            </div>
        </div>

        <div v-if="!showExpenses" class="form-field">
            <span
                style="text-decoration: underline; cursor: pointer"
                @click="showExpenses = true"
            >
                Do you track expenses on your trips?
            </span>
        </div>

        <!-- expenses -->
        <h3 v-if="showExpenses" class="field-title">Expenses</h3>
        <div v-if="showExpenses" class="two-fields">
            <div class="form-field">
                <label for="expenses">Total expenses:</label>
                <input
                    id="expenses"
                    v-model="form.expenses.value"
                    type="number"
                />
                <FormError :errors="errors.expenses" />
            </div>
            <div class="form-field">
                <label for="share">Your share (between 0 & 1):</label>
                <input
                    id="share"
                    v-model="form.share.value"
                    type="number"
                    max="100"
                />
                <small>
                    If you didn't share this expense, you should leave it at 1.
                </small>
                <FormError :errors="errors.share" />
            </div>
        </div>

        <div class="actions">
            <div v-if="deletePending" class="confirmation">
                Are you sure you want to delete this trip?
            </div>
            <Bttn v-if="!deletePending" id="submit-trip" type="submit">
                Save
            </Bttn>
            <Bttn
                v-if="canDelete && !deletePending"
                class="outlined"
                @click="deletePending = true"
            >
                Delete
            </Bttn>
            <Bttn
                v-if="deletePending"
                class="outlined"
                @click="deletePending = false"
            >
                Cancel
            </Bttn>
            <Bttn v-if="deletePending" @click="emitDelete">Confirm</Bttn>
            <router-link v-if="!deletePending" :to="backLink">
                <Bttn class="outlined">Back</Bttn>
            </router-link>
        </div>
    </form>
</template>

<script>
import BaseForm from "js/components/base/BaseForm.js";
import EnumOptions from "js/components/forms/EnumOptions";
import EnumRadio from "js/components/forms/EnumRadio";
import FormError from "js/components/forms/FormError";
import ImageUpload from "js/components/forms/ImageUpload";
import QuickLocations from "js/components/forms/QuickLocations.vue";
import { useUserStore } from "js/stores/userStore";
import { useUtilsStore } from "js/stores/utilsStore";
import Dropdown from "../common/Dropdown.vue";

export default {
    name: "TripForm",
    components: {
        Dropdown,
        FormError,
        EnumOptions,
        EnumRadio,
        ImageUpload,
        QuickLocations,
    },
    extends: BaseForm,
    props: {
        trip: {
            type: Object,
            default: undefined,
        },
    },
    data() {
        const convertDate = (date) =>
            new Date(date).toISOString().substring(0, 10);
        return {
            userStore: null,
            companionOptions: [],
            showMot: false,
            showExpenses: false,
            form: {
                name: {
                    value: null,
                },
                privacy: {
                    value: "personal",
                },
                start_date: {
                    value: null,
                    convert: convertDate,
                },
                end_date: {
                    value: null,
                    convert: convertDate,
                },
                notes: {
                    value: null,
                },
                reason: {
                    value: null,
                },
                companions: {
                    value: [],
                },
                travel_agency: {
                    value: null,
                },
                start_mot: {
                    value: null,
                },
                start_mot_count: {
                    value: 1,
                },
                end_mot: {
                    value: null,
                },
                end_mot_count: {
                    value: 1,
                },
                expenses: {
                    value: null,
                },
                share: {
                    value: 1,
                },
                user_image_file: {
                    value: null,
                },
                locations: {
                    value: null,
                },
            },
        };
    },
    async mounted() {
        this.userStore = useUserStore();
        this.companionOptions = await useUtilsStore().companions;
        this.userStore.refreshUser().then(() => {
            this.showExpenses =
                this.userStore.user.preferences.track_trip_expenses;
            this.showMot = this.userStore.user.preferences.track_mot;
        });
    },
    methods: {
        setEndDate(event) {
            if (this.form.end_date.value !== null) {
                return;
            }
            this.form.end_date.value = event.target.value;
        },
        handleFileChange(e) {
            if (e.target.files.length && e.target.files[0]) {
                this.form.user_image_file.value = e.target.files[0];
            }
        },
    },
};
</script>
